import { IDrgBreakdownInCents, IFileData } from "../CreateFeeSchedule";

/**
 * Map of normalized CSV header → DrgBreakdownInCents key on the frontend.
 * These headers come from Hospital Inpatient CMS files emitted by the backend
 * pricer (see clear-api `cms_pricer/output.ts` for the source-of-truth list).
 * Non-CMS files (OPPS, ASC, physician fee schedules) don't carry these columns
 * and the resulting `drg` object stays empty — passed as `undefined` so the
 * backend treats the row as non-DRG.
 */
const DRG_HEADER_MAP: Record<string, keyof IDrgBreakdownInCents> = {
  capital_disproportionate_share_hospital: "capitalDsh",
  capital_indirect_medical_education: "capitalIme",
  operating_disproportionate_share_hospital: "opsDsh",
  operating_indirect_medical_education: "opsIme",
  direct_medical_education: "opsDirectMedicalEducation",
  uncompensated_care_amount: "opsUncompensatedCare",
  readmission_adjustment: "opsReadmission",
  // Note: 'Value-Based' contains a hyphen which our normalizer preserves.
  "value-based_purchasing_adjustment": "opsValueBased",
  new_technology: "opsNewTechnology",
  other_pps_total: "otherPps",
};

function parseDollarsToCents(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = parseFloat(value.toString().replace(/[$,]/g, ""));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export const normalizeKeys = (data: any[]): IFileData[] => {
  return data.map((row: { [x: string]: any }) => {
    const normalizedRow: IFileData = {
      priceInCents: 0,
      description: "",
      cptCode: "",
    };
    const drg: IDrgBreakdownInCents = {};
    let drgHasAny = false;

    Object.entries(row).forEach(([key, value]) => {
      const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, "_");
      if (normalizedKey === "rate") {
        normalizedRow.priceInCents = Math.round(
          parseFloat(value.toString().replace(/[$,]/g, "")) * 100
        );
      } else if (normalizedKey === "cpt_name") {
        normalizedRow.description = value.toString();
      } else if (normalizedKey === "cpt_code") {
        normalizedRow.cptCode = value.toString();
      } else if (normalizedKey in DRG_HEADER_MAP) {
        const drgKey = DRG_HEADER_MAP[normalizedKey];
        drg[drgKey] = parseDollarsToCents(value);
        drgHasAny = true;
      }
    });

    // Only attach `drg` when the file actually had at least one breakdown
    // column — keeps the network payload clean for non-CMS files.
    if (drgHasAny) {
      normalizedRow.drg = drg;
    }
    return normalizedRow;
  });
};
